import { useMemo } from "react";
import { View, Text, Pressable, ScrollView, Platform } from "react-native";
import { MapPin, Medal } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useNetworkSiblings } from "@louise/providers/hooks/useOrganization";
import { useUserLocation } from "@louise/clinics/hooks/useUserLocation";
import { usePatient } from "@louise/patients/hooks/usePatient";
import {
  calculateDistance,
  formatDistanceForCountry,
} from "@louise/providers/utils/distance";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSmartBack } from "../../hooks/useSmartBack";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { BackLink, cardSurface } from "@louise/moon-ui";
import { colors } from "@louise/theme/tokens";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";

type Props = NativeStackScreenProps<AccessStackParamList, "NetworkLocations">;

function normalizeClinicName(name: string, network?: string | null): string {
  if (!network) return name;
  const escaped = network.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = name.match(new RegExp(`^${escaped}\\s*[-–—]?\\s*(.+)$`, "i"));
  if (match && match[1]) {
    return `${network} – ${match[1].trim()}`;
  }
  return name;
}

export default function NetworkLocationsScreen({ navigation, route }: Props) {
  const capturePress = usePressCapture("NetworkLocationsScreen");
  const handleBack = useSmartBack({
    fallbackTab: "Access",
    fallbackScreen: "FindCare",
  });
  const { clinicId, networkName } = route.params;
  const siblings = useNetworkSiblings(clinicId, networkName);
  const { location: userLocation } = useUserLocation();
  const { data: patient } = usePatient();

  const userAgeBand = useMemo(() => {
    if (!patient?.dateOfBirth) return null;
    const dob = new Date(patient.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    if (age < 35) return "<35";
    if (age <= 37) return "35-37";
    if (age <= 40) return "38-40";
    return ">40";
  }, [patient?.dateOfBirth]);

  const siblingsWithDistance = useMemo(() => {
    return siblings.map((s) => {
      let dist: string | null = null;
      if (userLocation && s.location) {
        const d = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          s.location.lat,
          s.location.lng,
        );
        dist = formatDistanceForCountry(d, s.address.country);
      }
      const successRate = userAgeBand
        ? s.outcomes?.successRates?.find(
            (r) =>
              r.ageBand === userAgeBand &&
              typeof r.rate === "number" &&
              r.rate > 0,
          )
        : null;
      return { ...s, dist, successRate: successRate?.rate ?? null };
    });
  }, [siblings, userLocation, userAgeBand]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header — matches ClinicDetailScreen */}
        <View style={{ marginBottom: 20 }}>
          <BackLink
            label="Back"
            onPress={handleBack}
            style={{ marginBottom: 8 }}
          />

          <Text
            className="font-dm-sans"
            style={
              {
                fontSize: 38,
                fontWeight: "700",
                color: colors.deepBrown,
                lineHeight: 42,
                letterSpacing: -0.5,
                ...(Platform.OS === "web" && {
                  ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }),
              } as Record<string, unknown>
            }
          >
            {networkName} locations
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textFaint,
              marginTop: 4,
            }}
          >
            {siblingsWithDistance.length} location
            {siblingsWithDistance.length !== 1 ? "s" : ""}
          </Text>
        </View>

        {/* Location cards */}
        <View style={{ gap: 10 }}>
          {siblingsWithDistance.map((sibling) => {
            const city = [sibling.address.city, sibling.address.state]
              .filter(Boolean)
              .join(", ");
            return (
              <Pressable
                key={sibling.id}
                onPress={() => {
                  capturePress(
                    pressLabel(normalizeClinicName(sibling.name, networkName)),
                    "network_clinic_row",
                  );
                  navigation.push("ClinicDetail", { clinicId: sibling.id });
                }}
                style={{
                  ...cardSurface(),
                  padding: 16,
                  gap: 4,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                    lineHeight: 18,
                  }}
                  numberOfLines={2}
                >
                  {normalizeClinicName(sibling.name, networkName)}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textFaint,
                  }}
                >
                  {city}
                </Text>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 4,
                  }}
                >
                  {sibling.dist && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <MapPin size={11} color={EMBER.colors.textFaint} />
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 11, color: EMBER.colors.textFaint }}
                      >
                        {sibling.dist}
                      </Text>
                    </View>
                  )}
                  {sibling.successRate != null && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <Medal size={11} color={EMBER.colors.warm} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11,
                          fontWeight: "600",
                          color: EMBER.colors.warm,
                        }}
                      >
                        {Math.round(sibling.successRate)}% success
                      </Text>
                    </View>
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
